// src/services/tabStatePersistence.js
//
// OBJECT-TABS-PERSIST-LAST-SELECTION
//
// Thin load/save helpers for per-group and per-instance tab memory using
// localStorage (survives tab close and browser restart).
//
// Two storage shapes:
//   • Group-level (e.g. Market Plan) — plain string under a single versioned key.
//   • Per-instance (e.g. Location Detail, Lease Detail) — a small {[id]: tab}
//     map under a single versioned key, bounded to MAX_INSTANCE_ENTRIES.
//
// Public API:
//   loadLastTab(groupKey, instanceId?)  → string | null
//   saveLastTab(groupKey, tab, instanceId?)
//
// groupKey values: "plan" | "location" | "lease"
//
// Versioned storage keys are exported so callers can list/purge them if needed.
// Naming convention: ripple_tab_<group>_v1  (bump suffix on breaking shape change).

import { log } from "../utils/logger.js";

// ── Storage key constants ─────────────────────────────────────────────────────
export const TAB_KEY_PLAN     = "ripple_tab_plan_v1";
export const TAB_KEY_LOCATION = "ripple_tab_location_v1";
export const TAB_KEY_LEASE    = "ripple_tab_lease_v1";

const GROUP_KEYS = {
  plan:     TAB_KEY_PLAN,
  location: TAB_KEY_LOCATION,
  lease:    TAB_KEY_LEASE,
};

// Per-instance map is capped so localStorage doesn't grow unboundedly when the
// user visits many different locations or leases over time.
const MAX_INSTANCE_ENTRIES = 200;

function storageKey(groupKey) {
  const k = GROUP_KEYS[groupKey];
  if (!k) {
    log.error(`tabStatePersistence: unknown groupKey=${JSON.stringify(groupKey)}`);
  }
  return k ?? null;
}

/**
 * Load the last-selected tab for a group or a specific instance within a group.
 *
 * @param {string} groupKey  – "plan" | "location" | "lease"
 * @param {string} [instanceId] – when provided, looks up the per-instance map;
 *                                omit for group-level (single-tab) storage.
 * @returns {string | null}
 */
export function loadLastTab(groupKey, instanceId) {
  try {
    const key = storageKey(groupKey);
    if (!key) return null;

    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;

    if (instanceId !== undefined && instanceId !== null) {
      // Per-instance map: {[id]: tab}
      const map = JSON.parse(raw);
      if (!map || typeof map !== "object" || Array.isArray(map)) return null;
      const val = map[String(instanceId)];
      return typeof val === "string" && val.length > 0 ? val : null;
    } else {
      // Group-level: plain string
      if (typeof raw === "string" && raw.length > 0 && !raw.startsWith("{")) {
        return raw;
      }
      // Guard against accidentally reading a per-instance map as group-level
      return null;
    }
  } catch (err) {
    log.error("tabStatePersistence: loadLastTab failed", { err: String(err), groupKey, instanceId });
    return null;
  }
}

/**
 * Save the last-selected tab for a group or a specific instance within a group.
 *
 * @param {string} groupKey  – "plan" | "location" | "lease"
 * @param {string} tab       – the tab key to persist
 * @param {string} [instanceId] – when provided, writes to the per-instance map.
 */
export function saveLastTab(groupKey, tab, instanceId) {
  try {
    const key = storageKey(groupKey);
    if (!key) return;
    if (typeof tab !== "string" || tab.length === 0) {
      log.error(`tabStatePersistence: saveLastTab invalid tab=${JSON.stringify(tab)}`);
      return;
    }

    if (instanceId !== undefined && instanceId !== null) {
      // Per-instance map
      let map = {};
      try {
        const raw = window.localStorage.getItem(key);
        if (raw !== null) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            map = parsed;
          }
        }
      } catch {
        // Corrupt data — start fresh
        map = {};
      }

      // Evict oldest entries if map is at capacity (simple: drop all but most recent)
      const entries = Object.entries(map);
      if (entries.length >= MAX_INSTANCE_ENTRIES) {
        // Keep the most-recent MAX_INSTANCE_ENTRIES - 1 entries + the new one
        const keep = entries.slice(-(MAX_INSTANCE_ENTRIES - 1));
        map = Object.fromEntries(keep);
      }

      map[String(instanceId)] = tab;
      window.localStorage.setItem(key, JSON.stringify(map));
    } else {
      // Group-level: plain string
      window.localStorage.setItem(key, tab);
    }
  } catch (err) {
    log.error("tabStatePersistence: saveLastTab failed", { err: String(err), groupKey, instanceId });
  }
}
